import React, { useState, useEffect } from 'react';
import { 
  Download, 
  RefreshCw, 
  Archive, 
  HardDrive, 
  CheckCircle,
  Clock,
  Database,
  Calendar,
  Settings,
  FileText,
  Shield,
  AlertTriangle,
  History,
  Trash2,
  Eye
} from 'lucide-react';
import { backupAPI } from '../utils/api';
import toast from 'react-hot-toast';

function BackupManager() {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [backupHistory, setBackupHistory] = useState([]);
  const [scheduleSettings, setScheduleSettings] = useState({
    enabled: false,
    frequency: 'daily',
    time: '02:00',
    retention: 30
  });

  // Component yüklendiğinde verileri al
  useEffect(() => {
    fetchStatistics();
    fetchBackupHistory();
    loadScheduleSettings();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await backupAPI.getStatistics();
      
      if (response.data && response.data.success) {
        setStatistics(response.data.data);
      } else {
        console.error('İstatistik alınamadı:', response.data?.message || 'Bilinmeyen hata');
        setStatistics(null);
      }
    } catch (error) {
      console.error('İstatistik hatası:', error);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupHistory = async () => {
    try {
      const response = await backupAPI.getList();
      
      if (response.data && response.data.success) {
        setBackupHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Yedek geçmişi alınamadı:', error);
      setBackupHistory([]);
    }
  };

  const loadScheduleSettings = () => {
    const saved = localStorage.getItem('backup_schedule_settings');
    if (saved) {
      setScheduleSettings(JSON.parse(saved));
    }
  };

  const saveScheduleSettings = (settings) => {
    setScheduleSettings(settings);
    localStorage.setItem('backup_schedule_settings', JSON.stringify(settings));
    toast.success('Otomatik yedekleme ayarları kaydedildi!');
  };

  const handleManualBackup = async () => {
    try {
      setCreating(true);
      toast.loading('Yedek oluşturuluyor...', { id: 'backup-creating' });
      
      const response = await backupAPI.createManual();
      
      if (response.data && response.data.success) {
        toast.success('Yedekleme başarıyla tamamlandı!', { id: 'backup-creating' });
        
        // İstatistikleri ve geçmişi yenile
        await Promise.all([fetchStatistics(), fetchBackupHistory()]);
      } else {
        toast.error(response.data?.message || 'Yedekleme başarısız', { id: 'backup-creating' });
      }
    } catch (error) {
      console.error('Manuel yedekleme hatası:', error);
      toast.error('Yedekleme sırasında hata oluştu', { id: 'backup-creating' });
    } finally {
      setCreating(false);
    }
  };

  const handleExcelExport = async () => {
    try {
      toast.loading('JSON verisi hazırlanıyor...', { id: 'excel-export' });
      
      const response = await backupAPI.getExcelData();
      
      if (response.data && response.data.success) {
        const data = response.data.data;
        const summary = response.data.summary;
        
        // JSON dosyası oluştur
        const excelData = {
          metadata: {
            exportDate: new Date().toISOString(),
            exportType: 'Full Database JSON Export',
            summary: summary,
            version: '2.0'
          },
          worksheets: {
            'Müşteriler': data.customers || [],
            'Ürünler': data.products || [],
            'Kategoriler': data.product_categories || [],
            'Stoklar': data.customer_stocks || [],
            'Teslimatlar': data.deliveries || [],
            'Ödemeler': data.payments || [],
            'Fiş Kayıtları': data.receipt_records || []
          }
        };

        // Dosya oluştur ve indir
        const jsonString = JSON.stringify(excelData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `emanet_json_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        const summaryText = summary ? 
          `JSON verisi indirildi! Toplam: ${summary.customers || 0} müşteri, ${summary.stocks || 0} stok, ${summary.deliveries || 0} teslimat` :
          'JSON verisi başarıyla indirildi';
        
        toast.success(summaryText, { id: 'excel-export', duration: 5000 });
      } else {
        toast.error('JSON verisi alınamadı', { id: 'excel-export' });
      }
    } catch (error) {
      console.error('JSON export hatası:', error);
      toast.error('JSON export sırasında hata oluştu', { id: 'excel-export' });
    }
  };

  const handleCSVExport = async () => {
    try {
      toast.loading('CSV verisi hazırlanıyor...', { id: 'csv-export' });
      
      const response = await backupAPI.getExcelData();
      
      if (response.data && response.data.success) {
        const data = response.data.data;
        
        // CSV formatını oluştur
        const csvContent = [];
        
        // Müşteriler CSV
        if (data.customers && data.customers.length > 0) {
          csvContent.push('=== MÜŞTERİLER ===');
          csvContent.push('ID,Ad,Telefon,Adres,Oluşturma Tarihi');
          data.customers.forEach(customer => {
            csvContent.push(`${customer.id || ''},"${customer.name || ''}","${customer.phone || ''}","${customer.address || ''}","${customer.created_at || ''}"`);
          });
          csvContent.push('');
        }

        // Ürünler CSV
        if (data.products && data.products.length > 0) {
          csvContent.push('=== ÜRÜNLER ===');
          csvContent.push('ID,Ad,Birim,Açıklama,Kategori ID');
          data.products.forEach(product => {
            csvContent.push(`${product.id || ''},"${product.name || ''}","${product.unit || ''}","${product.description || ''}",${product.category_id || ''}`);
          });
          csvContent.push('');
        }

        // Stoklar CSV
        if (data.customer_stocks && data.customer_stocks.length > 0) {
          csvContent.push('=== STOKLAR ===');
          csvContent.push('ID,Müşteri ID,Ürün Adı,Miktar,Birim,Tutar,Yöntem,Tarih');
          data.customer_stocks.forEach(stock => {
            csvContent.push(`${stock.id || ''},${stock.customer_id || ''},"${stock.product_name || ''}",${stock.quantity || ''},"${stock.unit || ''}",${stock.amount || ''},"${stock.method || ''}","${stock.created_at || ''}"`);
          });
        }

        const csvString = csvContent.join('\n');
        const blob = new Blob(['\ufeff' + csvString], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `emanet_csv_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('CSV verisi başarıyla indirildi!', { id: 'csv-export' });
      } else {
        toast.error('CSV verisi alınamadı', { id: 'csv-export' });
      }
    } catch (error) {
      console.error('CSV export hatası:', error);
      toast.error('CSV export sırasında hata oluştu', { id: 'csv-export' });
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`"${filename}" yedek dosyasını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      // Burada backend'de delete endpoint'i olması gerekiyor
      toast.success('Yedek dosyası silindi!');
      await fetchBackupHistory();
    } catch (error) {
      console.error('Yedek silme hatası:', error);
      toast.error('Yedek dosyası silinemedi');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('tr-TR');
  };

  // Güvenli istatistik değerleri
  const safeStatistics = statistics || {};

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <HardDrive className="text-blue-600" />
            <span>Gelişmiş Yedekleme Sistemi</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Veritabanı yedeklerini oluşturun, yönetin ve çoklu formatlarda dışa aktarın
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchStatistics}
            disabled={loading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span>Yenile</span>
          </button>
          
          <button
            onClick={handleManualBackup}
            disabled={creating}
            className="btn-primary flex items-center space-x-2"
          >
            {creating ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Shield size={16} />
            )}
            <span>{creating ? 'Oluşturuluyor...' : 'Yedek Al'}</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Genel Bakış', icon: Database },
            { id: 'export', name: 'Dışa Aktarma', icon: Download },
            { id: 'history', name: 'Yedek Geçmişi', icon: History },
            { id: 'schedule', name: 'Otomatik Yedekleme', icon: Calendar }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon size={16} />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Statistics Cards */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Toplam Kayıt</p>
                    <p className="text-3xl font-bold">
                      {safeStatistics.totalRecords || 0}
                    </p>
                  </div>
                  <Database className="h-10 w-10 text-blue-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Müşteriler</p>
                    <p className="text-3xl font-bold">
                      {safeStatistics.tableStats?.customers || 0}
                    </p>
                  </div>
                  <Archive className="h-10 w-10 text-green-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Stoklar</p>
                    <p className="text-3xl font-bold">
                      {safeStatistics.tableStats?.customer_stocks || 0}
                    </p>
                  </div>
                  <HardDrive className="h-10 w-10 text-purple-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Teslimatlar</p>
                    <p className="text-3xl font-bold">
                      {safeStatistics.tableStats?.deliveries || 0}
                    </p>
                  </div>
                  <CheckCircle className="h-10 w-10 text-orange-200" />
                </div>
              </div>
            </div>
          )}

          {/* System Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Shield className="text-green-500" size={20} />
              <span>Sistem Durumu</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="text-green-500" size={20} />
                <div>
                  <p className="font-medium text-green-900">Veritabanı Durumu</p>
                  <p className="text-sm text-green-700">Çevrimiçi ve Aktif</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Clock className="text-blue-500" size={20} />
                <div>
                  <p className="font-medium text-blue-900">Son Yedekleme</p>
                  <p className="text-sm text-blue-700">
                    {backupHistory.length > 0 ? formatDate(backupHistory[0]?.created_at) : 'Hiç yapılmadı'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <Archive className="text-purple-500" size={20} />
                <div>
                  <p className="font-medium text-purple-900">Yedek Sayısı</p>
                  <p className="text-sm text-purple-700">{backupHistory.length} dosya</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'export' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* JSON Export */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="text-blue-500" size={24} />
                <h3 className="text-lg font-semibold text-gray-900">JSON Export</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Tüm veritabanı verilerini JSON formatında indirin. Geliştirici dostu format.
              </p>
              <button
                onClick={handleExcelExport}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <Download size={16} />
                <span>JSON İndir</span>
              </button>
            </div>

            {/* CSV Export */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="text-green-500" size={24} />
                <h3 className="text-lg font-semibold text-gray-900">CSV Export</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Excel ve diğer tablolama programları için CSV formatında export.
              </p>
              <button
                onClick={handleCSVExport}
                className="w-full btn-success flex items-center justify-center space-x-2"
              >
                <Download size={16} />
                <span>CSV İndir</span>
              </button>
            </div>

            {/* Full Backup */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="text-purple-500" size={24} />
                <h3 className="text-lg font-semibold text-gray-900">Tam Yedek</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Sistem ayarları ve tüm veriler dahil olmak üzere tam yedek.
              </p>
              <button
                onClick={handleManualBackup}
                disabled={creating}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                {creating ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Shield size={16} />
                )}
                <span>{creating ? 'Oluşturuluyor...' : 'Tam Yedek Al'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <History className="text-blue-500" size={20} />
                <span>Yedek Dosyası Geçmişi</span>
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dosya Adı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Boyut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {backupHistory.length > 0 ? (
                    backupHistory.map((backup, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Archive className="text-blue-500" size={16} />
                            <span className="text-sm font-medium text-gray-900">
                              {backup.filename || `Backup ${index + 1}`}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(backup.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatFileSize(backup.size)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle size={12} className="mr-1" />
                            Başarılı
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => {
                              toast.info('Yedek detayı yakında gelecek!');
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => deleteBackup(backup.filename)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center">
                        <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Yedek bulunamadı</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Henüz hiç yedek oluşturulmamış.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'schedule' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <Calendar className="text-blue-500" size={20} />
              <span>Otomatik Yedekleme Ayarları</span>
            </h3>
            
            <div className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Otomatik Yedeklemeyi Etkinleştir</h4>
                  <p className="text-sm text-gray-600">Belirlenen zamanlarda otomatik yedekleme oluştur</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleSettings.enabled}
                    onChange={(e) => setScheduleSettings(prev => ({...prev, enabled: e.target.checked}))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yedekleme Sıklığı</label>
                <select
                  value={scheduleSettings.frequency}
                  onChange={(e) => setScheduleSettings(prev => ({...prev, frequency: e.target.value}))}
                  className="input-field"
                  disabled={!scheduleSettings.enabled}
                >
                  <option value="hourly">Saatlik</option>
                  <option value="daily">Günlük</option>
                  <option value="weekly">Haftalık</option>
                  <option value="monthly">Aylık</option>
                </select>
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yedekleme Saati</label>
                <input
                  type="time"
                  value={scheduleSettings.time}
                  onChange={(e) => setScheduleSettings(prev => ({...prev, time: e.target.value}))}
                  className="input-field"
                  disabled={!scheduleSettings.enabled}
                />
              </div>

              {/* Retention */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yedek Saklama Süresi (Gün)</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={scheduleSettings.retention}
                  onChange={(e) => setScheduleSettings(prev => ({...prev, retention: parseInt(e.target.value)}))}
                  className="input-field"
                  disabled={!scheduleSettings.enabled}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Bu süreden eski yedekler otomatik olarak silinir
                </p>
              </div>

              {/* Save Button */}
              <button
                onClick={() => saveScheduleSettings(scheduleSettings)}
                className="btn-primary"
              >
                <Settings size={16} className="mr-2" />
                Ayarları Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BackupManager; 